import { FC } from 'react';
import { useTheme } from '../../context/ThemeContext';

interface SkeletonProps {
  className?: string;
  width?: string | number;
  height?: string | number;
  variant?: 'text' | 'circular' | 'rectangular' | 'rounded';
  animation?: 'pulse' | 'wave' | 'none';
}

export const Skeleton: FC<SkeletonProps> = ({
  className = '',
  width,
  height,
  variant = 'rounded',
  animation = 'pulse',
}) => {
  const { isDark } = useTheme();

  const baseClass = isDark ? 'bg-slate-700' : 'bg-gray-200';
  const animationClass = animation === 'pulse' ? 'animate-pulse' : animation === 'wave' ? 'animate-shimmer' : '';

  const variantClass = {
    text: 'rounded',
    circular: 'rounded-full',
    rectangular: 'rounded-none',
    rounded: 'rounded-lg',
  }[variant];

  const style: React.CSSProperties = {};
  if (width) style.width = typeof width === 'number' ? `${width}px` : width;
  if (height) style.height = typeof height === 'number' ? `${height}px` : height;

  return <div className={`${baseClass} ${animationClass} ${variantClass} ${className}`} style={style} />;
};

// Account Summary Skeleton (content only - used inside existing pane)
export const AccountSummarySkeleton: FC = () => {
  const { isDark } = useTheme();

  return (
    <>
      {/* Welcome Section */}
      <div className={`rounded-lg p-4 mb-4 ${isDark ? 'bg-slate-700/50' : 'bg-gray-100'}`}>
        <Skeleton width="60%" height={24} className="mb-2" />
        <Skeleton width="40%" height={16} />
      </div>

      {/* Account Breakdown Cards */}
      <div className="flex flex-col sm:flex-row gap-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className={`flex-1 rounded-lg ${isDark ? 'bg-slate-700/30' : 'bg-gray-50'} p-3`}>
            <Skeleton width="50%" height={12} className="mb-2" />
            <Skeleton width="80%" height={24} />
          </div>
        ))}
      </div>
    </>
  );
};

// Chart Skeleton
export const ChartSkeleton: FC = () => {
  const { isDark } = useTheme();
  const bgColor = isDark ? 'bg-slate-800' : 'bg-white';

  return (
    <div className={`flex-1 flex flex-col rounded-lg ${bgColor} shadow-md overflow-hidden min-h-[400px]`}>
      {/* Chart Header */}
      <div className="flex justify-between items-center p-4">
        <div className="flex flex-col gap-1">
          <Skeleton width={180} height={28} />
          <Skeleton width={120} height={16} />
        </div>
        <div className="flex gap-2">
          {['1Y', '3Y', '5Y', 'All'].map((period) => (
            <Skeleton key={period} width={40} height={32} variant="rounded" />
          ))}
        </div>
      </div>

      {/* Chart Area */}
      <div className="flex-1 p-4 pt-0">
        <div
          className={`w-full h-full min-h-[300px] rounded-lg ${
            isDark ? 'bg-slate-700/50' : 'bg-gray-100'
          } flex items-end justify-around p-4`}
        >
          {/* Simulated chart bars */}
          {[40, 65, 45, 80, 55, 70, 60, 85, 50, 75, 65, 90].map((h, i) => (
            <div key={i} className="flex flex-col items-center gap-2 flex-1">
              <Skeleton width="80%" height={`${h}%`} className="animate-pulse" />
            </div>
          ))}
        </div>
      </div>

      {/* Chart Legend */}
      <div
        className={`flex items-center justify-center gap-6 p-3 border-t ${
          isDark ? 'border-slate-700' : 'border-gray-200'
        }`}
      >
        <div className="flex items-center gap-2">
          <Skeleton width={12} height={12} variant="circular" />
          <Skeleton width={60} height={14} />
        </div>
        <div className="flex items-center gap-2">
          <Skeleton width={12} height={12} variant="circular" />
          <Skeleton width={80} height={14} />
        </div>
      </div>
    </div>
  );
};

// Table Skeleton - for data tables
export const TableSkeleton: FC<{ rows?: number; columns?: number }> = ({ rows = 5, columns = 5 }) => {
  const { isDark } = useTheme();
  const bgColor = isDark ? 'bg-slate-800' : 'bg-white';
  const borderColor = isDark ? 'border-slate-700' : 'border-gray-200';

  return (
    <div className={`${bgColor} rounded-lg overflow-hidden`}>
      {/* Table Header */}
      <div className={`flex gap-4 p-4 border-b ${borderColor}`}>
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={i} width={i === 0 ? '25%' : '15%'} height={16} />
        ))}
      </div>

      {/* Table Rows */}
      {Array.from({ length: rows }).map((_row, rowIndex) => (
        <div key={rowIndex} className={`flex gap-4 p-4 border-b ${borderColor} last:border-b-0`}>
          {Array.from({ length: columns }).map((_col, colIndex) => (
            <Skeleton key={colIndex} width={colIndex === 0 ? '25%' : '15%'} height={14} />
          ))}
        </div>
      ))}
    </div>
  );
};

// Wine Card Skeleton - for wine card lists
const WineCardSkeleton: FC = () => {
  const { isDark } = useTheme();
  const bgColor = isDark ? 'bg-slate-800' : 'bg-white';
  const borderColor = isDark ? 'border-slate-700' : 'border-gray-200';

  return (
    <div className={`${bgColor} rounded-lg border ${borderColor} p-4 flex gap-4`}>
      {/* Wine Image */}
      <Skeleton width={60} height={80} variant="rounded" />

      {/* Wine Info */}
      <div className="flex-1 flex flex-col justify-center gap-2">
        <Skeleton width="70%" height={18} />
        <Skeleton width="40%" height={14} />
        <Skeleton width="30%" height={14} />
      </div>

      {/* Price/Value */}
      <div className="flex flex-col items-end justify-center gap-2">
        <Skeleton width={80} height={18} />
        <Skeleton width={50} height={14} />
      </div>
    </div>
  );
};

// Wine Card List Skeleton
export const WineCardListSkeleton: FC<{ count?: number }> = ({ count = 5 }) => {
  return (
    <div className="flex flex-col gap-3">
      {Array.from({ length: count }).map((_, i) => (
        <WineCardSkeleton key={i} />
      ))}
    </div>
  );
};

// Slideout Content Skeleton - for slideout panels
export const SlideoutSkeleton: FC = () => {
  const { isDark } = useTheme();

  return (
    <div className="flex flex-col gap-6 p-4">
      {/* Product Image */}
      <div className="flex justify-center">
        <Skeleton width={120} height={160} variant="rounded" />
      </div>

      {/* Product Title */}
      <div className="text-center">
        <Skeleton width="80%" height={24} className="mx-auto mb-2" />
        <Skeleton width="50%" height={16} className="mx-auto" />
      </div>

      {/* Price Section */}
      <div className={`rounded-lg p-4 ${isDark ? 'bg-slate-700/50' : 'bg-gray-100'}`}>
        <div className="flex justify-between items-center mb-3">
          <Skeleton width={100} height={14} />
          <Skeleton width={80} height={20} />
        </div>
        <div className="flex justify-between items-center">
          <Skeleton width={80} height={14} />
          <Skeleton width={60} height={16} />
        </div>
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-2 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className={`rounded-lg p-3 ${isDark ? 'bg-slate-700/30' : 'bg-gray-50'}`}>
            <Skeleton width="60%" height={12} className="mb-2" />
            <Skeleton width="80%" height={18} />
          </div>
        ))}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 mt-4">
        <Skeleton width="50%" height={44} variant="rounded" />
        <Skeleton width="50%" height={44} variant="rounded" />
      </div>
    </div>
  );
};

// Profile Skeleton - for profile/account pages
export const ProfileSkeleton: FC = () => {
  const { isDark } = useTheme();
  const bgColor = isDark ? 'bg-slate-800' : 'bg-white';

  return (
    <div className={`${bgColor} rounded-lg p-6`}>
      {/* Avatar and Name */}
      <div className="flex items-center gap-4 mb-6">
        <Skeleton width={80} height={80} variant="circular" />
        <div className="flex-1">
          <Skeleton width="40%" height={24} className="mb-2" />
          <Skeleton width="60%" height={16} />
        </div>
      </div>

      {/* Form Fields */}
      <div className="space-y-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i}>
            <Skeleton width={100} height={14} className="mb-2" />
            <Skeleton width="100%" height={44} variant="rounded" />
          </div>
        ))}
      </div>

      {/* Save Button */}
      <div className="mt-6">
        <Skeleton width={120} height={44} variant="rounded" />
      </div>
    </div>
  );
};

// Notification List Skeleton
export const NotificationListSkeleton: FC<{ count?: number }> = ({ count = 5 }) => {
  const { isDark } = useTheme();
  const bgColor = isDark ? 'bg-slate-800' : 'bg-white';
  const borderColor = isDark ? 'border-slate-700' : 'border-gray-200';

  return (
    <div className="flex flex-col">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className={`${bgColor} border-b ${borderColor} p-4 flex gap-4`}>
          <Skeleton width={40} height={40} variant="circular" />
          <div className="flex-1">
            <Skeleton width="70%" height={16} className="mb-2" />
            <Skeleton width="90%" height={14} className="mb-1" />
            <Skeleton width="30%" height={12} />
          </div>
        </div>
      ))}
    </div>
  );
};

// Transaction List Skeleton
export const TransactionListSkeleton: FC<{ count?: number }> = ({ count = 5 }) => {
  const { isDark } = useTheme();
  const bgColor = isDark ? 'bg-slate-800' : 'bg-white';
  const borderColor = isDark ? 'border-slate-700' : 'border-gray-200';

  return (
    <div className={`${bgColor} rounded-lg overflow-hidden`}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className={`flex items-center justify-between p-4 border-b ${borderColor} last:border-b-0`}>
          <div className="flex items-center gap-3">
            <Skeleton width={40} height={40} variant="circular" />
            <div>
              <Skeleton width={150} height={16} className="mb-1" />
              <Skeleton width={100} height={12} />
            </div>
          </div>
          <div className="text-right">
            <Skeleton width={80} height={18} className="mb-1" />
            <Skeleton width={60} height={12} />
          </div>
        </div>
      ))}
    </div>
  );
};

// Payment Card List Skeleton
export const PaymentCardListSkeleton: FC<{ count?: number }> = ({ count = 2 }) => {
  const { isDark } = useTheme();
  const bgColor = isDark ? 'bg-slate-800' : 'bg-white';
  const borderColor = isDark ? 'border-slate-700' : 'border-gray-200';

  return (
    <div className="flex flex-col gap-3">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className={`${bgColor} rounded-lg border ${borderColor} p-4 flex items-center gap-4`}>
          <Skeleton width={50} height={32} variant="rounded" />
          <div className="flex-1">
            <Skeleton width="40%" height={16} className="mb-1" />
            <Skeleton width="30%" height={12} />
          </div>
          <Skeleton width={24} height={24} variant="circular" />
        </div>
      ))}
    </div>
  );
};

// Generic Content Skeleton - flexible content placeholder
export const ContentSkeleton: FC<{ lines?: number; className?: string }> = ({ lines = 3, className = '' }) => {
  return (
    <div className={`flex flex-col gap-3 ${className}`}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} width={i === lines - 1 ? '60%' : '100%'} height={16} />
      ))}
    </div>
  );
};

// Discover/Event Card Skeleton
const EventCardSkeleton: FC = () => {
  const { isDark } = useTheme();
  const bgColor = isDark ? 'bg-slate-800' : 'bg-white';
  const borderColor = isDark ? 'border-slate-700' : 'border-gray-200';

  return (
    <div className={`${bgColor} rounded-lg border ${borderColor} overflow-hidden`}>
      {/* Image */}
      <Skeleton width="100%" height={180} variant="rectangular" />

      {/* Content */}
      <div className="p-4">
        <Skeleton width="80%" height={20} className="mb-2" />
        <Skeleton width="100%" height={14} className="mb-1" />
        <Skeleton width="70%" height={14} className="mb-3" />
        <div className="flex justify-between items-center">
          <Skeleton width={80} height={12} />
          <Skeleton width={60} height={12} />
        </div>
      </div>
    </div>
  );
};

// Grid of Event Cards Skeleton
export const EventGridSkeleton: FC<{ count?: number }> = ({ count = 6 }) => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {Array.from({ length: count }).map((_, i) => (
        <EventCardSkeleton key={i} />
      ))}
    </div>
  );
};

// Form Skeleton - for form loading states
export const FormSkeleton: FC<{ fields?: number }> = ({ fields = 4 }) => {
  return (
    <div className="flex flex-col gap-5 p-4">
      {Array.from({ length: fields }).map((_, i) => (
        <div key={i}>
          <Skeleton width={120} height={14} className="mb-2" />
          <Skeleton width="100%" height={44} variant="rounded" />
        </div>
      ))}
      <Skeleton width={140} height={44} variant="rounded" className="mt-4" />
    </div>
  );
};
